import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {NEAR_BLACK} from 'ui-library/Colors';
import {capitalize} from 'util/NameUtil';

import type * as Types from '../graphql-client/__generated/graphqlGlobalTypes';

interface Term {
  value: Types.BaseUnitType;
  exponent: number;
}

export interface UnitTypeTermsDisplayProps {
  terms: Array<Term>;
}

function formatTermAsTextElement(term: {value: string; exponent: number}, key: number): JSX.Element {
  return (
    <span key={key}>
      {capitalize(term.value)}
      {(term.exponent > 1 || term.exponent < -1) && <sup>{Math.abs(term.exponent)}</sup>}
    </span>
  );
}

function joinTerms(terms: Array<Term>): Array<JSX.Element> {
  const result = [];
  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];
    result.push(formatTermAsTextElement(term, 2 * i));
    if (i < terms.length - 1) {
      result.push(<span key={2 * i + 1}> · </span>);
    }
  }
  return result;
}

export default function UnitTypeTermsDisplay({terms}: UnitTypeTermsDisplayProps): JSX.Element {
  const positiveTerms = terms.filter((term) => term.exponent > 0);
  const negativeTerms = terms.filter((term) => term.exponent < 0);
  const positivesAsText = joinTerms(positiveTerms);
  const negativesAsText = joinTerms(negativeTerms);
  const numerator = (
    <span {...getTestProps({component: 'UnitTypeTermsDisplay', element: 'numerator'})}>
      {positiveTerms.length > 0 ? positivesAsText : 1}
    </span>
  );
  const denominator =
    negativeTerms.length > 0 ? (
      <span {...getTestProps({component: 'UnitTypeTermsDisplay', element: 'denominator'})}>
        {negativesAsText}
      </span>
    ) : null;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.center)}>
        {numerator}
        {denominator && <hr className={css(styles.fractionLine)} />}
        {denominator}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: 8,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fractionLine: {
    borderTop: 1,
    borderColor: NEAR_BLACK,
    marginTop: 3.5,
    marginBottom: 3.5,
    borderStyle: 'solid',
    width: '100%',
  },
});
